/*eslint-disable*/
import cloneDeep from 'lodash/cloneDeep';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

// @mui/material components
import { makeStyles, withStyles } from '@mui/styles';

// material ui icons
import Assignment from '@mui/icons-material/Assignment';
import Permission from '@mui/icons-material/LockOpen';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

// core components
import CustomInput from 'components/CustomInput/CustomInput.js';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import ReactTable from 'components/ReactTable/ReactTable.js';

import {
  primaryColor,
  secondaryColor,
  whiteColor,
} from '@themes/defaultThemeProps';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardIcon from 'components/Card/CardIcon.js';
import Button from 'components/CustomButtons/Button.js';

// style for this view
import styles from 'assets/jss/material-dashboard-pro-react/views/roleEditStyle.js';

// Redux
import { useGetPermissionsQuery } from 'api/role.api';
import { selectViewIsDirty, setViewIsDirty } from 'features/ux/uxSlice';

const useStyles = makeStyles(styles);
let pendingRedirectUrl = null;

const getEmptyRole = (scope, application, permissions) => {
  const defaultPermissions = permissions
    .filter((p) => p.application === application)
    .map((permission) => {
      return { name: permission.name, value: permission.default };
    });
  return {
    scope: scope,
    name: '',
    description: '',
    permissions: defaultPermissions,
  };
};

// create superset of permissions, filtered by scope, with values from the current role
const getRolePermissions = (role, permissions, scope) => {
  const application = scope === 'NT' ? 'NT' : 'Console';
  const rolePermissions = permissions
    .filter((p) => p.application === application)
    .map((entry, key) => {
      // look to see if permission is already defined for the role
      var roleData = role.permissions.find((pd) => pd.name === entry.name);
      // if permission doesn't have a role value, then use the dictionary default value
      return {
        name: entry.name,
        value: roleData ? roleData.value : entry.default,
      };
    });
  return rolePermissions;
};

const AntSwitch = withStyles(() => ({
  root: {
    width: 28,
    height: 16,
    padding: 0,
    display: 'flex',
  },
  switchBase: {
    padding: 2,
    color: secondaryColor[1],
    '&$checked': {
      transform: 'translateX(12px)',
      color: whiteColor,
      '& + $track': {
        opacity: 1,
        backgroundColor: primaryColor[0],
        borderColor: primaryColor[0],
      },
    },
  },
  thumb: {
    width: 12,
    height: 12,
    boxShadow: 'none',
  },
  track: {
    border: `1px solid ${primaryColor[0]}`,
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor: whiteColor,
  },
  checked: {},
}))(Switch);

export default function Role(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const location = useLocation();
  const viewIsDirty = useSelector(selectViewIsDirty);
  const { data: fetchedPermissions } = useGetPermissionsQuery();
  const permissions = fetchedPermissions.filter((role) => {
    return role.application === 'Console' || role.application === 'NT';
  });

  const newRole = location.state.new;
  const application = location.state.scope === 'NT' ? 'NT' : 'Console';
  let role;
  if (newRole) {
    role = getEmptyRole(location.state.scope, application, permissions);
  } else {
    role = location.state.role;
  }
  const scope = role.scope;

  const [name, setName] = useState(role.name);
  const [nameState, setNameState] = useState('');
  const [nameError, setNameError] = useState('');
  const [description, setDescription] = useState(role.description);
  const [descriptionState, setDescriptionState] = useState('');
  const [descriptionError, setDescriptionError] = useState('');

  // function that verifies if a string has a given length or not
  const verifyLength = (value, original, length, required = true) => {
    if (value && value.length >= length) {
      if (value !== original) dispatch(setViewIsDirty(true));
      return true;
    } else if (!required && (!value || value.length === 0)) {
      if (value !== original) dispatch(setViewIsDirty(true));
      return true;
    }
    return false;
  };

  const getInitialPermissions = () => {
    return getRolePermissions(role, permissions, scope).map((permission) =>
      ['True', true, 'true'].includes(permission.value)
    );
  };

  const [checked, _setChecked] = useState(getInitialPermissions);
  const checkedRef = useRef(checked);
  // use proxy setter to keep ref synchronized with state
  const setChecked = (c) => {
    _setChecked(c);
    checkedRef.current = c;
    dispatch(setViewIsDirty(true));
  };

  const handleToggle = (index) => {
    const currentValue = checkedRef.current[index];
    const newChecked = [...checkedRef.current];
    newChecked[index] = !currentValue;
    setChecked(newChecked);
    const newData = [...dataRef.current];
    const newRow = { ...newData[index] };
    newRow.permission = permissionSwitch(newChecked[index], index);
    newData[index] = newRow;
    setData(newData);
  };

  const permissionSwitch = (value, index) => {
    return (
      <Typography component='div' className={classes.formControl}>
        <Grid component='label' container alignItems='center' spacing={2}>
          <Grid item>False</Grid>
          <Grid item>
            <AntSwitch
              id='booleanValue'
              label='booleanValue'
              checked={value === 'true' || value === true}
              onChange={(e) => handleToggle(index)}
            />
          </Grid>
          <Grid item>True</Grid>
        </Grid>
      </Typography>
    );
  };

  const initialData = () => {
    return getRolePermissions(role, permissions, scope).map((entry, key) => {
      var permissionData = permissions.find((pd) => pd.name === entry.name);
      const parameterRow = {
        id: key,
        application: permissionData.application,
        category: permissionData.category,
        function: {
          function: permissionData.name,
          description: permissionData.description,
        },
        permission: permissionSwitch(checkedRef.current[key], key),
      };
      return parameterRow;
    });
  };

  const [data, _setData] = useState(initialData);
  const dataRef = useRef(data);

  const setData = (d) => {
    dataRef.current = d;
    _setData(d);
  };

  const formatFunctionCell = ({ value }) => {
    return (
      <div>
        <span className={classes.functionName}>{value.function}</span>
        <span className={classes.functionDescription}>{value.description}</span>
      </div>
    );
  };

  useEffect(() => {
    dispatch(setViewIsDirty(false));
  }, []);

  useEffect(() => {
    if (pendingRedirectUrl) {
      props.history.push(pendingRedirectUrl);
      pendingRedirectUrl = null;
    }
  }, [viewIsDirty]);

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color='rose' icon>
            <CardIcon color='rose'>
              <Assignment />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>Role Information</h4>
          </CardHeader>
          <CardBody>
            <GridContainer>
              <GridItem xs={12}>
                <CustomInput
                  helperText={
                    nameState === 'error'
                      ? nameError ?? 'An error has occurred'
                      : false
                  }
                  success={nameState === 'success'}
                  error={nameState === 'error'}
                  labelText='Role Name'
                  id='name'
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    defaultValue: name,
                    onChange: (event) => {
                      if (verifyLength(event.target.value, name, 3)) {
                        setNameState('success');
                        setNameError('');
                      } else {
                        setNameState('error');
                        setNameError(
                          'The field should be at least 3 characters long'
                        );
                      }
                      setName(event.target.value);
                    },
                    autoComplete: 'off',
                  }}
                />
                <CustomInput
                  helperText={
                    descriptionState === 'error'
                      ? descriptionError ?? 'An error has occurred'
                      : false
                  }
                  success={descriptionState === 'success'}
                  error={descriptionState === 'error'}
                  labelText='Role Description'
                  id='description'
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    defaultValue: description,
                    onChange: (event) => {
                      if (verifyLength(event.target.value, description, 3)) {
                        setDescriptionState('success');
                        setDescriptionError('');
                      } else {
                        setDescriptionState('error');
                        setDescriptionError(
                          'The field should be at least 3 characters long'
                        );
                      }
                      setDescription(event.target.value);
                    },
                    autoComplete: 'off',
                  }}
                />
                <Button
                  color='secondary'
                  onClick={() => props.history.push('/admin/baseroles')}
                  className={classes.registerButton}
                >
                  Back
                </Button>
              </GridItem>
            </GridContainer>
          </CardBody>
        </Card>
      </GridItem>
      <GridItem xs={12}>
        <Card>
          <CardHeader color='rose' icon>
            <CardIcon color='rose'>
              <Permission />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>Permissions</h4>
          </CardHeader>
          <CardBody>
            <ReactTable
              columns={[
                {
                  Header: 'Category',
                  accessor: 'category',
                  disableSortBy: true,
                  width: 100,
                },
                {
                  Header: 'Function',
                  accessor: 'function',
                  Cell: formatFunctionCell,
                  filter: 'function',
                  disableSortBy: true,
                  width: 200,
                },
                {
                  Header: 'Permission',
                  accessor: 'permission',
                  disableFilters: true,
                  disableSortBy: true,
                  width: 100,
                  style: {
                    marginBottom: 20,
                  },
                },
              ]}
              data={dataRef.current}
              options={{
                pageSize: dataRef.current.length,
              }}
            />
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
